/**
 * IssueSharesPhase - 株式発行フェーズUIコンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * @see FRONTEND.md
 * @see src/types/ui-interfaces.ts
 */

import type { IssueSharesPhaseProps } from '../../types/ui-interfaces';
import { useTranslations, interpolate } from '../../i18n';

/**
 * Phase 1: 株式発行フェーズUI
 *
 * 機能:
 * - 現在の株式数・現金を表示
 * - 発行株数の入力
 * - 発行ボタン
 */
export function IssueSharesPhase({
  player,
  maxIssueCount,
  maxSharesLimit,
  isMyTurn,
  onIssueShares,
  helpTextKey,
  // 入力状態は親コンポーネントで管理
  issueCount = 0,
  onIssueCountChange,
}: IssueSharesPhaseProps & {
  issueCount?: number;
  onIssueCountChange?: (count: number) => void;
}) {
  const t = useTranslations();

  // helpTextKey からヘルプテキストを取得
  const getHelpTexts = (): string[] | undefined => {
    if (!helpTextKey) return undefined;
    if (helpTextKey === 'barbados') {
      return [t.issueShares.barbados.helpMaxPerTurn];
    }
    if (helpTextKey === 'detroit') {
      return [t.issueShares.detroit.helpMaxShares];
    }
    return undefined;
  };
  const helpTexts = getHelpTexts();

  const handleIssueClick = () => {
    onIssueShares(issueCount);
  };

  const handleDecrement = () => {
    if (issueCount > 0) {
      onIssueCountChange?.(issueCount - 1);
    }
  };

  const handleIncrement = () => {
    if (issueCount < maxIssueCount) {
      onIssueCountChange?.(issueCount + 1);
    }
  };

  return (
    <div className="space-y-3">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : (
        <>
          <label className="text-sm text-muted-foreground">{interpolate(t.issueShares.numberOfShares, { max: maxIssueCount })}</label>
          {/* +/- ボタンと Confirm */}
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary px-3 py-1.5 text-sm font-bold"
              onClick={handleDecrement}
              disabled={issueCount <= 0}
            >
              −
            </button>
            <span className="w-8 text-center font-mono text-lg font-bold">{issueCount}</span>
            <button
              className="btn-secondary px-3 py-1.5 text-sm font-bold"
              onClick={handleIncrement}
              disabled={issueCount >= maxIssueCount}
            >
              +
            </button>
            <button
              className="btn-primary flex-1 py-1.5 text-sm"
              onClick={handleIssueClick}
              disabled={issueCount < 0 || player.sharesIssued + issueCount > maxSharesLimit}
            >
              {t.common.confirm}
            </button>
          </div>

          {/* 受け取り金額 */}
          <div className="text-sm text-success font-medium">
            {interpolate(t.issueShares.cashReward, { amount: issueCount * 5 })}
          </div>

          {/* ヘルプテキスト（propsで指定された場合のみ表示） */}
          {helpTexts && helpTexts.length > 0 && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer hover:text-foreground">{t.issueShares.helpTitle}</summary>
              <ul className="mt-2 ml-4 list-disc space-y-1">
                {helpTexts.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

export default IssueSharesPhase;
